import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';
import { ArrowLeft, ExternalLink, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast.ts';
import { usePEClinicIds } from '@/hooks/pe/usePEClinicIds.ts';
import { DateOfBirthPicker } from '@/components/ui/date-of-birth-picker.tsx';
import { format } from 'date-fns';
import UnifiedHeader from '@/components/common/UnifiedHeader.tsx';
import { serviceFactory } from '@/services/core/serviceFactory.ts';
import { PatientEnrollerService, type PEPatient } from '@/services/features/PatientEnrollerService.ts';

interface PEPatientListSearchableProps {
  type: 'followup' | 'deferred';
}

const PEPatientListSearchable: React.FC<PEPatientListSearchableProps> = ({ type }) => {
  const { user, hasuraToken } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { clinicIds, isLoading: clinicIdsLoading } = usePEClinicIds();
  const [patients, setPatients] = useState<PEPatient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('Name');
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const title = type === 'followup' ? 'Follow up Patients' : 'Deferred Patients';
  const statusFilter = type === 'followup' ? 'Follow Up' : 'Deferred';

  useEffect(() => {
    if (clinicIds.length > 0) {
      fetchPatients();
    }
  }, [type, user?.id, hasuraToken, clinicIds]);

  // Clear search fields when search type changes
  useEffect(() => {
    setSearchTerm('');
    setSelectedDate(undefined);
  }, [searchBy]);

  const fetchPatients = async () => {
    if (!user?.id || !hasuraToken || clinicIds.length === 0) return;

    try {
      setIsLoading(true);
      setError(null);

      // Update service context with current auth info
      serviceFactory.updateGlobalContext({
        hasuraToken,
        userId: user?.id,
        token: hasuraToken, // Using hasuraToken for GraphQL queries
        userType: user?.type
      });

      const patientEnrollerService = serviceFactory.getService(PatientEnrollerService);
      
      // Prepare search filters
      const searchFilters = searchTerm || selectedDate ? {
        searchBy,
        searchTerm,
        selectedDate
      } : undefined;

      const response = await patientEnrollerService.fetchPETaskPatientList(
        clinicIds,
        statusFilter,
        searchFilters
      );

      if (response.success && response.data) {
        setPatients(response.data.pep_vw_patient_enroller_task_patient_list || []);
      } else {
        throw new Error(response.errors?.join(', ') || `Failed to fetch ${type} patients`);
      }
    } catch (err) {
      console.error(`Error fetching ${type} patients:`, err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      toast({
        title: "Error",
        description: `Failed to load ${type} patients`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    fetchPatients();
  };

  const handlePatientAction = (patient: PEPatient) => {
    navigate('/patient-details', { 
      state: { 
        patient: {
          id: patient.pep_patient_id,
          full_name: patient.full_name,
          dob: patient.dob,
          clinic_name: patient.clinic_name,
          ccm_status: patient.ccm_status_value,
          rpm_status: patient.rpm_status_value,
          provider_name: patient.provider_name
        }
      }
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Appointment Not Set';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatAppointmentTime = (dateString: string, timezone: string) => {
    if (!dateString) return 'Appointment Not Set';
    try {
      const hasTZ = /[+-]\d{2}:\d{2}$/.test(dateString) || dateString.endsWith('Z');
      const normalized = (hasTZ ? dateString : `${dateString.replace(' ', 'T')}Z`).replace(' ', 'T');
      const utcDate = new Date(normalized);
      
      const date = utcDate.toLocaleDateString('en-US', {
        timeZone: timezone || 'US/Eastern',
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
      });
      
      const time = utcDate.toLocaleTimeString('en-US', {
        timeZone: timezone || 'US/Eastern',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      return `${date} ${time}`;
    } catch (error) {
      return formatDate(dateString);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <UnifiedHeader 
        title={title}
        userType="PE"
        showFollowUp={false}
        showDeferred={false}
        onFollowUpClick={() => {}}
        onDeferredClick={() => {}}
        showTimer={true}
      />
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            Patient Enrollment Specialist (PES) - {title}
          </h1>
        </div>

        {/* Search Controls */}
        <Card className="mb-6 bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Select value={searchBy} onValueChange={setSearchBy}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Name">Name</SelectItem>
                  <SelectItem value="DOB">Date of Birth</SelectItem>
                  <SelectItem value="Phone Number">Phone Number</SelectItem>
                  <SelectItem value="Provider Name">Provider Name</SelectItem>
                  <SelectItem value="Appointment Date">Appointment Date</SelectItem>
                  <SelectItem value="Clinic">Clinic Name</SelectItem>
                </SelectContent>
              </Select>

              {(searchBy === 'DOB' || searchBy === 'Appointment Date') ? (
                <DateOfBirthPicker
                  date={selectedDate}
                  onDateChange={setSelectedDate}
                  placeholder={`Select ${searchBy === 'DOB' ? 'Date of Birth' : 'Appointment Date'}`}
                  type={searchBy === 'DOB' ? 'dob' : 'appointment'}
                  className="w-60"
                />
              ) : (
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-80"
                />
              )}
              
              <Button 
                onClick={handleSearch}
                disabled={isLoading}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              >
                {isLoading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              {title} ({patients.length} patients found)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading || clinicIdsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-500 mb-4">{error}</p>
                <Button onClick={fetchPatients}>
                  Retry
                </Button>
              </div>
            ) : patients.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  No {type} patients found matching your search criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table id={`pe-${type}-patients-table`} data-testid={`pe-${type}-patients-table`}>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-700">Last Appointment</TableHead>
                      <TableHead className="font-semibold text-gray-700">Name</TableHead>
                      <TableHead className="font-semibold text-gray-700">Date of Birth</TableHead>
                      <TableHead className="font-semibold text-gray-700">Phone</TableHead>
                      <TableHead className="font-semibold text-gray-700">Provider</TableHead>
                      <TableHead className="font-semibold text-gray-700">Clinic Name</TableHead>
                      <TableHead className="font-semibold text-gray-700">CCM Status</TableHead>
                      <TableHead className="font-semibold text-gray-700">RPM Status</TableHead>
                      <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patients.map((patient) => (
                      <TableRow key={patient.pep_patient_id} className="hover:bg-gray-50/50">
                        <TableCell>{formatAppointmentTime(patient.consented_appointment_date_time, patient.patient_clinic_timezone)}</TableCell>
                        <TableCell className="font-medium">{patient.full_name}</TableCell>
                        <TableCell>{patient.dob}</TableCell>
                        <TableCell>{patient.phone_number || 'N/A'}</TableCell>
                        <TableCell>{patient.provider_name}</TableCell>
                        <TableCell>{patient.clinic_name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            patient.ccm_status_value === 'Enrolled' 
                              ? 'bg-green-100 text-green-800' 
                              : patient.ccm_status_value === 'Deferred'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {patient.ccm_status_value}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            patient.rpm_status_value === 'Enrolled' 
                              ? 'bg-green-100 text-green-800' 
                              : patient.rpm_status_value === 'Deferred'
                              ? 'bg-orange-100 text-orange-800'
                              : patient.rpm_status_value === 'Follow Up'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {patient.rpm_status_value}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePatientAction(patient)}
                            className="h-8 w-8 p-0"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PEPatientListSearchable;